/**
 * TeachFirst — optional teacher-paced pre-game walkthrough.
 *
 * Loads the SAME items the upcoming game would (same /api endpoints, same
 * filters from the same URL search params), then lets the teacher flip
 * through them one at a time. Footer "Start Game" jumps straight into the
 * round with the exact settings the teacher just configured.
 *
 * No DB writes. No scoring. Pure presentation.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { NavBar } from '../components/NavBar';
import { ChevronLeft, ChevronRight, Play, X, SkipForward } from 'lucide-react';
import { TeachFirstRenderer, classifyArchetype } from '../components/teach/TeachFirstRenderer';

export default function TeachFirst() {
  const { game } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);

  const mode = searchParams.get('mode') || 'school';
  const difficulty = searchParams.get('difficulty') || 'introductory';

  // Mirror UnifiedGamePlay's fetch flow exactly so Teach First shows what the
  // game will actually play.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      const useLessonBank = searchParams.get('lessonBank') === '1';
      const lessonId = searchParams.get('lessonId');

      const fetchFromSharedPool = () => {
        const params = { mode, game, difficulty };
        if (mode === 'school') {
          params.grade = searchParams.get('grade');
          params.month = searchParams.get('month');
        }
        if (mode === 'level') {
          params.level = searchParams.get('level');
          const topic = searchParams.get('topic');
          if (topic) params.topic = topic;
        }
        if (mode === 'topic') {
          params.topic = searchParams.get('topic');
        }
        const itemId = searchParams.get('item_id');
        if (itemId) params.item_id = itemId;
        return axios.get(`${API}/content/browse`, { params }).then(r => {
          let data = r.data.items || [];
          if (data.length === 0) {
            const fallback = { ...params };
            delete fallback.difficulty;
            return axios.get(`${API}/content/browse`, { params: fallback }).then(r2 => r2.data.items || []);
          }
          return data;
        });
      };

      const fetchItems = useLessonBank && lessonId
        ? axios.get(`${API}/programs/lessons/${lessonId}/game-items/${game}`)
            .then(r => r.data.items || [])
            .catch(() => fetchFromSharedPool())
        : fetchFromSharedPool();

      try {
        let data = await fetchItems;
        if (cancelled) return;
        // Apply the same per-game type filters UnifiedGamePlay uses.
        const qType = searchParams.get('questionType');
        if (game === 'horloge_folle' && qType && qType !== 'all') {
          const typeMap = {
            numbers: ['see_produce_french'],
            time: ['see_clock_produce_french', 'time_reading', 'read_set_clock', 'time_with_future'],
            listening: ['hear_type_number', 'hear_type_time', 'hear_produce_time'],
            french_math: ['math_in_french'],
          };
          const allowed = typeMap[qType] || [];
          if (allowed.length) data = data.filter(it => allowed.includes(it.mode));
        }
        if (game === 'la_tour' && qType && qType !== 'all') {
          const typeMap = {
            articles: ['article_selection', 'preposition_selection'],
            conjugation: ['conjugation_drill', 'verb_selection', 'auxiliary_selection', 'agreement_selection'],
            tenses: ['tense_sorting', 'sentence_transformation'],
            vocabulary: ['word_image_speed_match', 'comparison_selection'],
          };
          const allowed = typeMap[qType] || [];
          if (allowed.length) data = data.filter(it => allowed.includes(it.exercise_type));
        }
        if (game === 'tri_express' && qType && qType !== 'all') {
          const typeMap = {
            vocabulary: ['vocabulary_category'],
            gender: ['gender'],
            tenses: ['tense', 'tense_sorting'],
            grammar: ['comparison_type', 'conjunction_type', 'verb_type', 'auxiliary_selection', 'grammar_category', 'opinion_vs_fact', 'grammar', 'agreement_form', 'agreement_number', 'auxiliary_in_context'],
          };
          const allowed = typeMap[qType] || [];
          if (allowed.length) data = data.filter(it => allowed.includes(it.sort_type));
        }
        if (game === 'robot_balayeur') {
          const selectedIds = searchParams.get('selectedPassages');
          if (selectedIds) {
            const idSet = new Set(selectedIds.split(','));
            data = data.filter(it => idSet.has(it.item_id));
          }
        }
        setItems(data);
        setIdx(0);
        setLoading(false);
      } catch (_e) {
        if (!cancelled) { setItems([]); setLoading(false); }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [game, mode, difficulty, searchParams]);

  const breadcrumbs = useMemo(() => ['Teach First', `${game}`.replace(/_/g, ' ')], [game]);

  const goBackToSetup = () => {
    const params = new URLSearchParams(searchParams);
    navigate(`/play/${game}/setup?${params}`);
  };
  const startGame = () => {
    // Strip teach-only state (none right now) and jump to the live game.
    const params = new URLSearchParams(searchParams);
    navigate(`/play/${game}?${params}`);
  };

  const total = items.length;
  const current = items[idx];
  const isShuffledArchetype = current && classifyArchetype(current) === 'mcq' && Array.isArray(current?.options) && current.options.length > 1;
  const prev = () => setIdx(i => Math.max(0, i - 1));
  const next = () => setIdx(i => Math.min(total - 1, i + 1));

  return (
    <div data-testid="teach-first-screen" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <NavBar items={breadcrumbs} onBack={goBackToSetup} />

      {/* Top action bar: progress + close */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'clamp(0.6rem, 1vw, 1rem) clamp(1rem, 1.5vw, 1.75rem)', borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
      }}>
        <div data-testid="teach-progress" style={{ fontFamily: 'Nunito', fontWeight: 700, color: 'var(--text-primary)', fontSize: 'clamp(1rem, 1.2vw, 1.25rem)' }}>
          {loading ? 'Loading items...' : (total === 0 ? 'No items' : `${idx + 1} / ${total}`)}
        </div>
        <button data-testid="teach-close" onClick={goBackToSetup} style={{
          display: 'flex', alignItems: 'center', gap: '0.35rem',
          padding: 'clamp(0.4rem, 0.7vw, 0.6rem) clamp(0.75rem, 1.1vw, 1.1rem)', borderRadius: '0.6rem',
          border: '1px solid var(--border)', background: 'var(--surface)',
          fontFamily: 'Nunito', fontSize: 'clamp(0.85rem, 1vw, 1.05rem)', cursor: 'pointer', color: 'var(--text-secondary)',
        }}>
          <X size={18} /> Close
        </button>
      </div>

      {/* Content area */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'clamp(1rem, 2vw, 2.5rem) clamp(0.75rem, 1.5vw, 1.5rem)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
        {loading ? (
          <div style={{ fontFamily: 'Fredoka', fontSize: 'clamp(1.1rem, 1.3vw, 1.5rem)', color: 'var(--text-secondary)' }}>Loading items...</div>
        ) : total === 0 ? (
          <div style={{ fontFamily: 'Fredoka', fontSize: 'clamp(1.1rem, 1.3vw, 1.5rem)', color: 'var(--text-secondary)' }}>No items match the current selection.</div>
        ) : (
          <TeachFirstRenderer item={current} />
        )}
      </div>

      {/* Bottom controls */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 'clamp(0.5rem, 0.8vw, 0.9rem)', padding: 'clamp(0.75rem, 1.2vw, 1.25rem) clamp(1rem, 1.5vw, 1.75rem)', borderTop: '1px solid var(--border)',
        background: 'var(--surface)', flexWrap: 'wrap', position: 'relative',
      }}>
        {isShuffledArchetype && (
          <div data-testid="teach-shuffle-note" style={{
            position: 'absolute', right: '1.25rem', bottom: '0.4rem',
            fontFamily: 'Nunito, sans-serif', fontSize: 'clamp(0.7rem, 0.8vw, 0.85rem)', fontStyle: 'italic',
            color: 'var(--text-secondary)', opacity: 0.7, pointerEvents: 'none',
            maxWidth: 320, textAlign: 'right',
          }}>
            Options shuffled in the live game.
          </div>
        )}
        <button data-testid="teach-prev" onClick={prev} disabled={idx === 0 || total === 0} style={{
          padding: 'clamp(0.6rem, 0.9vw, 0.9rem) clamp(0.9rem, 1.3vw, 1.5rem)', borderRadius: '0.7rem', border: '2px solid var(--border)',
          background: 'var(--surface)', fontFamily: 'Fredoka', fontSize: 'clamp(0.95rem, 1.15vw, 1.2rem)', fontWeight: 600,
          cursor: idx === 0 ? 'default' : 'pointer', opacity: idx === 0 ? 0.4 : 1,
          display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
        }}>
          <ChevronLeft size={20} /> Previous
        </button>
        <button data-testid="teach-skip" onClick={next} disabled={idx >= total - 1 || total === 0} style={{
          padding: 'clamp(0.6rem, 0.9vw, 0.9rem) clamp(0.9rem, 1.3vw, 1.5rem)', borderRadius: '0.7rem', border: '2px solid var(--border)',
          background: 'var(--surface)', fontFamily: 'Fredoka', fontSize: 'clamp(0.95rem, 1.15vw, 1.2rem)', fontWeight: 600,
          cursor: idx >= total - 1 ? 'default' : 'pointer', opacity: idx >= total - 1 ? 0.4 : 1,
          display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: 'var(--text-secondary)',
        }}>
          <SkipForward size={20} /> Skip
        </button>
        <button data-testid="teach-next" onClick={next} disabled={idx >= total - 1 || total === 0} style={{
          padding: 'clamp(0.6rem, 0.9vw, 0.9rem) clamp(0.9rem, 1.3vw, 1.5rem)', borderRadius: '0.7rem', border: '2px solid var(--border)',
          background: 'var(--surface)', fontFamily: 'Fredoka', fontSize: 'clamp(0.95rem, 1.15vw, 1.2rem)', fontWeight: 700,
          cursor: idx >= total - 1 ? 'default' : 'pointer', opacity: idx >= total - 1 ? 0.4 : 1,
          display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
        }}>
          Next <ChevronRight size={20} />
        </button>
        <div style={{ width: 1, height: 32, background: 'var(--border)', margin: '0 0.4rem' }} />
        <button data-testid="teach-start-game" onClick={startGame} disabled={total === 0} style={{
          padding: 'clamp(0.7rem, 1.1vw, 1rem) clamp(1.25rem, 1.8vw, 2rem)', borderRadius: '0.7rem', border: 'none',
          background: '#4C6EF5', color: 'white', fontFamily: 'Fredoka', fontSize: 'clamp(1rem, 1.25vw, 1.3rem)', fontWeight: 700,
          cursor: total === 0 ? 'default' : 'pointer', opacity: total === 0 ? 0.5 : 1,
          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          boxShadow: '0 6px 18px rgba(76,110,245,0.4)',
        }}>
          <Play size={20} /> Start Game
        </button>
      </div>
    </div>
  );
}
